import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { CatalogueService } from '@catalogue';
import { buildMultiQuoteHttpOptions } from '@common';
import { CONFIG } from '@config';
import { CustomerService } from '@customer';
import { map, Observable, switchMap, tap } from 'rxjs';
import { BasketItemApplianceDetails } from '../models/basket-item/basket-item-appliance-details.interface';
import { BasketItemQuoteDetails } from '../models/basket-item/basket-item-quote-details.interface';
import { BasketItemType } from '../models/basket-item/basket-item-type.enum';
import { BasketItem } from '../models/basket-item/basket-item.type';
import { Basket } from '../models/basket/basket.interface';
import { CustomerStatusRequest } from '../models/customer/customer-status-request.interface';
import { CustomerStatusResponse } from '../models/customer/customer-status-response.interface';
import { PaymentOption } from '../models/payment/payment-option.interface';
import { PaymentPromotions } from '../models/payment/payment-promotions.interface';
import { AddAppliancesToBasketRequest } from '../models/quote/add-appliances-to-basket-request.interface';
import { AddAppliancesToBasketResponse } from '../models/quote/add-appliances-to-basket-response.interface';
import { AddQuoteToBasketResponse } from '../models/quote/add-quote-to-basket-response';
import { QuoteRequest } from '../models/quote/quote-request.interface';
import { QuoteResponse } from '../models/quote/quote-response.interface';
import { RemoveAppliancesFromBasketRequest } from '../models/quote/remove-appliances-from-basket-request.interface';
import { SelectedAppliance } from '../models/selected-appliance/selected-appliance.interface';

@Injectable({ providedIn: 'root' })
export class BasketService {
  private readonly catalogueService = inject(CatalogueService);
  private readonly config = inject(CONFIG);
  private readonly clientBrandCode = this.config.clientBrandCode;
  private readonly customerService = inject(CustomerService);
  private readonly httpClient = inject(HttpClient);
  private clpEnabled = this.config.form?.clpEnabled;
  private multiPlanEnabled = Boolean(
    this.config.domainFeatures?.multiPlanEnabled,
  );

  private readonly _basket = signal<Basket | undefined>(undefined);
  private readonly _basketItem = signal<BasketItem | null>(null);
  private readonly _basketId = signal<string | null>(null);
  private readonly _initialBasket = signal<Basket | undefined>(undefined);
  readonly _showMultiPlanJourney = signal<boolean>(false);

  readonly basket = computed<Basket | undefined>(() => this._basket());
  readonly initialBasket = computed<Basket>(
    () => this._initialBasket() as Basket,
  );

  readonly basketItem = computed<BasketItem>(
    () => this._basketItem() as BasketItem,
  );

  readonly basketId = computed<string>(() => this._basketId() as string);

  readonly basketItemApplianceDetails = computed<BasketItemApplianceDetails>(
    () => this.basketItem()?.data.applianceDetails,
  );

  readonly basketItemQuoteDetails = computed<BasketItemQuoteDetails>(
    () => this.basketItem()?.data.quotes[0],
  );

  readonly basketItemQuoteDetailsPaymentOption = computed<PaymentOption>(
    () => this.basketItem()?.data.quotes[0].paymentOptions?.[0],
  );

  readonly basketItemQuotePromotions = computed<PaymentPromotions | undefined>(
    () =>
      (this.basketItem()?.data.quotes[0] as BasketItemQuoteDetails)?.promotions,
  );

  readonly basketItemQuoteHasDiscountedMonths = computed<boolean>(() => {
    const promotions = this.basketItemQuotePromotions();
    if (!promotions || promotions.promotionType === 'UNKNOWN') return false;
    const hasMonths = (promotions.discountedMonths ?? 0) > 0;
    const isNotFullDiscount = promotions.percentage !== 100;
    return hasMonths && isNotFullDiscount;
  });

  readonly basketItemQuoteHasFreeMonths = computed<boolean>(() => {
    const promotions = this.basketItemQuotePromotions();
    if (!promotions || promotions.promotionType === 'UNKNOWN') return false;
    const freeMonths = 12 - (promotions.months ?? 12) > 0;
    const isFullDiscount = promotions.percentage === 100;
    return freeMonths && isFullDiscount;
  });

  readonly basketItemQuoteFreeMonths = computed<number>(() => {
    const promotions = this.basketItemQuotePromotions();
    if (!promotions || promotions.promotionType === 'UNKNOWN') return 0;
    return 12 - (promotions?.months ?? 12);
  });

  readonly basketItemQuoteHasPromo = computed<boolean>(() => {
    return (
      this.basketItemQuoteHasDiscountedMonths() ||
      this.basketItemQuoteHasFreeMonths()
    );
  });

  readonly basketItemQuotePaymentOptionIsOneOff = computed<boolean>(() => {
    const paymentOption = this.basketItemQuoteDetailsPaymentOption();
    return paymentOption?.numPayments === 1;
  });

  readonly basketItemQuoteIsOneOffPayment = computed<boolean>(() => {
    return (
      !this.basketItemQuoteHasDiscountedMonths() &&
      this.basketItemQuotePaymentOptionIsOneOff()
    );
  });

  readonly basketItemQuoteOneOffPrice = computed<number | undefined>(() => {
    if (!this.basketItemQuoteIsOneOffPayment()) return undefined;
    return this.basketItemQuoteDetailsPaymentOption()?.fee;
  });

  readonly basketItemQuoteDiscountedMonths = computed<number | undefined>(
    () => this.basketItemQuotePromotions()?.discountedMonths,
  );

  readonly basketItemQuoteDiscountedMonthlyPayment = computed<
    number | undefined
  >(() => this.basketItemQuotePromotions()?.discountedMonthlyPayment);

  readonly basketItemQuoteFullMonthlyPayment = computed<number | undefined>(
    () => this.basketItemQuotePromotions()?.fullMonthlyPayment,
  );

  readonly basketContainsBundleAppliances = computed<boolean>(
    () =>
      (this.basketItem()?.data.applianceDetails.bundleAppliances.length ?? 0) >
      0,
  );

  readonly partsWarrantyInYears = computed(() => {
    const months = this.quoteRequest()?.partsWarranty ?? 0;
    const years = months / 12;
    return years === 1 ? '1 year' : years > 1 ? `${years} years` : null;
  });

  readonly isMultiPlanJourney = this._showMultiPlanJourney.asReadonly();

  private readonly _retrievedQuote = signal<QuoteResponse | undefined>(
    undefined,
  );

  readonly retrievedQuote = this._retrievedQuote.asReadonly();

  readonly retrievedQuoteId = computed(() => {
    const quote = this.retrievedQuote();
    return quote ? quote.item.quotes[0]?.quoteId : undefined;
  });

  readonly initialRegisteredAppliance = computed<SelectedAppliance | null>(
    () => ({
      applianceCode: this.catalogueService.steppedFormAppliance()
        ?.applianceCode as string,
      applianceName: this.catalogueService.steppedFormAppliance()
        ?.applianceName as string,
      isSelected: true,
      manufacturer: this.config.clientName,
      wasBoughtWithin12Months: false,
      purchasePrice: this.catalogueService
        .selectedAppliancePurchasePrice()
        .toString(),
      purchaseYear: this.catalogueService
        .selectedAppliancePurchaseDate()
        .getFullYear(),
      purchaseMonth: this.catalogueService
        .selectedAppliancePurchaseDate()
        .toLocaleString('default', {
          month: 'long',
        }),
    }),
  );

  readonly vm = computed(() => ({
    basketItemApplianceDetails: this.basketItemApplianceDetails(),
    basketItemQuoteDetails: this.basketItemQuoteDetails(),
    basketItemQuoteDetailsPaymentOption:
      this.basketItemQuoteDetailsPaymentOption(),
  }));

  // === Mutation methods ===
  setRetrievedQuote(quote: QuoteResponse): void {
    this._retrievedQuote.set(quote);
  }

  setBasket(basket: Basket): void {
    this._basket.set(basket);
    this._basketItem.set(basket.items[0]);
    this._basketId.set(basket.basketId);
  }

  setShowMultiPlanJourney(showMultiPlanJourney: boolean): void {
    this._showMultiPlanJourney.set(showMultiPlanJourney);
  }

  readonly isNewCustomer = this._showMultiPlanJourney.asReadonly();

  // === Payloads ===
  readonly quoteRequest = computed<QuoteRequest | undefined>(() => {
    const appliance = this.catalogueService.steppedFormAppliance();
    const applianceWarranty =
      this.catalogueService.steppedFormApplianceWarranty();
    const price = this.catalogueService.selectedAppliancePurchasePrice();
    const person = this.customerService.selectedPersonInfo();
    const date = this.catalogueService.selectedAppliancePurchaseDate();
    const modelNumber = this.catalogueService.selectedApplianceModelNumber();
    const serialNumber = this.catalogueService.selectedApplianceSerialNumber();

    const formattedDate = {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };

    if (!appliance) {
      return undefined;
    }

    let partsWarranty: number | undefined;

    if (this.config.partsWarrantyInMonths) {
      partsWarranty = this.config.partsWarrantyInMonths;
    } else {
      partsWarranty = appliance.partsWarranty;
    }

    return {
      applianceCode: appliance.applianceCode,
      brandCode: this.clientBrandCode,
      itemType: BasketItemType.Quote,
      ...(modelNumber && { modelNumber }),
      ...(serialNumber && { serialNumber }),
      purchaseDay: formattedDate.day,
      purchaseMonth: formattedDate.month,
      purchaseYear: formattedDate.year,
      purchasePrice: price,
      warranty: applianceWarranty,
      ...(partsWarranty && this.clpEnabled && { partsWarranty }),
      ...(!this.clpEnabled || this._showMultiPlanJourney() ? {} : { person }),
    };
  });

  // === API calls ===
  getV1Quote(): Observable<AddQuoteToBasketResponse> {
    const options = buildMultiQuoteHttpOptions(this.multiPlanEnabled);

    return this.httpClient
      .post<{
        result: AddQuoteToBasketResponse;
      }>(`${this.config.basketApi}/v1/quote`, this.quoteRequest(), options)
      .pipe(
        map((response) => response.result),
        tap((result) => {
          this.setBasket(result.basket);
          this._initialBasket.set(result.basket);
        }),
      );
  }

  getSubsequentV1Quote(
    payload: QuoteRequest | undefined,
  ): Observable<AddQuoteToBasketResponse> {
    const options = buildMultiQuoteHttpOptions(true);

    return this.httpClient
      .post<{
        result: AddQuoteToBasketResponse;
      }>(`${this.config.basketApi}/v1/quote`, payload, options)
      .pipe(
        map((response) => response.result),
        tap((result) => this.setBasket(result.basket)),
      );
  }

  getV2Quote(): Observable<QuoteResponse> {
    return this.httpClient
      .post<{
        result: QuoteResponse;
      }>(`${this.config.basketApi}/v2/quote`, this.quoteRequest())
      .pipe(
        map((response) => response.result),
        tap((quote) => this.setRetrievedQuote(quote)),
      );
  }

  getSingleApplianceQuote(
    clpEnabled: boolean,
  ): Observable<QuoteResponse | AddQuoteToBasketResponse> {
    return clpEnabled
      ? this.getV2Quote().pipe(switchMap(() => this.addQuoteToBasket()))
      : this.getV1Quote();
  }

  addQuoteToBasket(): Observable<AddQuoteToBasketResponse> {
    const quoteId = this.retrievedQuoteId();

    return this.httpClient
      .post<{
        result: AddQuoteToBasketResponse;
      }>(`${this.config.basketApi}/v2/addQuoteToBasket`, { quoteId })
      .pipe(
        map(
          (response: { result: AddQuoteToBasketResponse }) => response.result,
        ),
        tap((result: AddQuoteToBasketResponse) =>
          this.setBasket(result.basket),
        ),
      );
  }

  addAppliancesToBasket(
    payload: AddAppliancesToBasketRequest,
  ): Observable<AddAppliancesToBasketResponse> {
    const options = buildMultiQuoteHttpOptions(true);

    return this.httpClient
      .post<AddAppliancesToBasketResponse>(
        `${this.config.basketApi}/v1/add-appliances`,
        payload,
        options,
      )
      .pipe(
        tap((response: AddAppliancesToBasketResponse) =>
          this.setBasket(response.result),
        ),
      );
  }

  removeAppliancesFromBasket(): Observable<Basket> {
    const options = buildMultiQuoteHttpOptions(true);
    const payload: RemoveAppliancesFromBasketRequest = {
      appliances: this.basketItem().data.applianceDetails.bundleAppliances,
      basketId: this.basketId(),
      itemId: this.basketItem().itemId,
      itemType: BasketItemType.Quote,
    };
    return this.httpClient
      .post<{
        result: Basket;
      }>(`${this.config.basketApi}/v1/remove-appliances`, payload, options)
      .pipe(
        map((response) => response.result),
        tap((result: Basket) => this.setBasket(result)),
      );
  }

  getCustomerStatus(
    payload: CustomerStatusRequest,
  ): Observable<CustomerStatusResponse> {
    return this.httpClient
      .post<{
        result: CustomerStatusResponse;
      }>(`${this.config.userApi}/v1/get-customer-status`, payload)
      .pipe(map((response) => response.result));
  }
}
